import { useLayoutEffect, useMemo, useRef } from 'react';

import {
  useExplorationActions,
  useExplorationState,
  useFilteredVolumes,
} from '../../context/ExplorationContext';
import { useExplorationTooltipHandlers } from '../../hooks/useExplorationTooltipHandlers';
import { buildFullHierarchy } from '../../lib/hierarchy/buildFullHierarchy';
import { wrapFullHierarchyByAccount } from '../../lib/hierarchy/wrapByAccount';
import {
  applyExplorationCanvasEmptyState,
  clearExplorationCanvasContent,
  explorationCanvasEmptyVolumesMessage,
} from '../explorationCanvasChrome';
import { renderTreeView } from './renderTreeView';

export default function TreeCanvas() {
  const state = useExplorationState();
  const actions = useExplorationActions();
  const filtered = useFilteredVolumes();
  const containerRef = useRef<HTMLDivElement>(null);
  const { showTooltip, hideTooltip } = useExplorationTooltipHandlers();

  const tree = useMemo(() => {
    if (!filtered.length) return null;
    const opts = {
      secondary: state.secondary,
      tagKey: state.tagKey,
    };
    if (state.groupByAccount) {
      return wrapFullHierarchyByAccount(filtered, opts);
    }
    return buildFullHierarchy(filtered, opts);
  }, [filtered, state.secondary, state.tagKey, state.groupByAccount]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    if (!filtered.length || !tree) {
      hideTooltip();
      applyExplorationCanvasEmptyState(el, explorationCanvasEmptyVolumesMessage);
      el.classList.add('tree-canvas');
      return () => {
        clearExplorationCanvasContent(el);
        el.classList.remove('tree-canvas');
      };
    }

    return renderTreeView(el, {
      view: state.view,
      secondary: state.secondary,
      tagKey: state.tagKey,
      volumes: filtered,
      tree,
      onSelectVolume: actions.selectVolume,
      onSelectInstance: actions.selectInstance,
      showTooltip,
      hideTooltip,
    });
  }, [
    tree,
    filtered,
    state.view,
    state.secondary,
    state.tagKey,
    actions.selectVolume,
    actions.selectInstance,
    showTooltip,
    hideTooltip,
  ]);

  return <div ref={containerRef} className="exploration-canvas tree-canvas" />;
}
